import { useLatestCallback } from '@marketplace-web/shared/ui-state'

import { toParams, toUrlQuery } from '@marketplace-web/shared/utils'
import { CATALOG_URL } from 'constants/routes'
import { getSearchStartData } from '@marketplace-web/domain/search'
import {
  transformCatalogNavigationDtos,
  getCatalogInitializersParamsFromUrl,
  transformDynamicFilters,
  transformSelectedDynamicFilters,
  TransformedApiData,
} from '@marketplace-web/domain/catalog-data'
import { useFeatureSwitch } from '@marketplace-web/shared/feature-switches'

import { getCatalogInitializers } from 'data/api/get-catalog-initializers'
import { getCatalogSearchScope } from 'data/api/get-catalog-search-scope'

import {
  DataReducerAction,
  DtoReducerAction,
  DynamicFilterReducerAction,
  FilterReducerAction,
  GeneralReducerAction,
} from '../CatalogProvider/reducers/actionTypes'

import useCatalogContext from '../CatalogProvider/useCatalogContext'
import useFetchCatalogBrand from './useFetchCatalogBrand'
import useFetchCatalogConfigurations from './useFetchCatalogConfigurations'

const useFetchCatalogData = () => {
  const { state, dispatch } = useCatalogContext()
  const fetchCatalogBrand = useFetchCatalogBrand()
  useFetchCatalogConfigurations()

  const searchScopeEnabled = useFeatureSwitch('scoped_search_mvp_enabled')

  const fetchCatalogData = useLatestCallback(async ({ relativeUrl, searchParams }) => {
    let catalogIds: Array<number> | undefined
    const catalogInitializersFromUrl = getCatalogInitializersParamsFromUrl(
      relativeUrl,
      searchParams,
    )

    dispatch({
      type: DataReducerAction.FETCH_DATA_REQUEST,
      payload: { relativeUrl, searchParams },
    })

    if (catalogInitializersFromUrl.catalog) {
      const { catalog } = catalogInitializersFromUrl

      if (Array.isArray(catalog)) {
        catalogIds = catalog.map(value => parseInt(value, 10))
      } else {
        catalogIds = [parseInt(catalog, 10)]
      }
    }

    const { searchStartId, searchStartType } = getSearchStartData()
    if (
      searchStartId &&
      searchStartType &&
      searchScopeEnabled &&
      !catalogInitializersFromUrl.search_id &&
      catalogInitializersFromUrl.search_text
    ) {
      const searchScope = await getCatalogSearchScope({
        catalogId: catalogIds?.[0],
        searchText: String(catalogInitializersFromUrl.search_text),
        search_start_type: searchStartType,
        search_start_id: searchStartId,
      })

      if (!('errors' in searchScope)) {
        if (searchScope.catalog_id != null) {
          catalogInitializersFromUrl.catalog = [searchScope.catalog_id]
          catalogIds = [Number(searchScope.catalog_id)]

          const params = toParams(window.location.search)
          params.catalog = catalogInitializersFromUrl.catalog

          const updatedUrl = `${CATALOG_URL}?${toUrlQuery(params)}`
          window.history.replaceState(null, '', updatedUrl)
        }
      }
    }

    if (catalogIds) {
      dispatch({
        type: DynamicFilterReducerAction.UPDATE_TEMPORARY_CATALOG_IDS,
        payload: { temporaryCatalogIds: catalogIds },
      })
    }

    const response = await getCatalogInitializers(catalogInitializersFromUrl)

    if ('errors' in response) {
      dispatch({ type: DataReducerAction.FETCH_DATA_FAILURE, payload: undefined })
    } else {
      const { dtos, filters } = response
      const currentBrand = state.catalogBrand.brand
      const data: TransformedApiData = {
        catalogTree: dtos.catalogs,
        catalogs: transformCatalogNavigationDtos(dtos.catalogs),
      }

      if (dtos.dynamicFilters && dtos.selectedDynamicFilters) {
        data.dynamicFilters = transformDynamicFilters(dtos.dynamicFilters)
        data.selectedDynamicFilters = transformSelectedDynamicFilters(dtos.selectedDynamicFilters)
      }

      dispatch({
        type: DtoReducerAction.CHANGE_DTO,
        payload: { dtos },
      })

      dispatch({
        type: DataReducerAction.FETCH_DATA_SUCCESS,
        payload: { catalogTree: data.catalogTree, catalogs: data.catalogs },
      })

      dispatch({
        type: DynamicFilterReducerAction.FETCH_DYNAMIC_FILTERS_SUCCESS,
        payload: {
          dynamicFilters: data.dynamicFilters || [],
          selectedDynamicFilters: data.selectedDynamicFilters || [],
        },
      })

      dispatch({
        type: FilterReducerAction.SET_INITIAL_FILTERS,
        payload: { filters },
      })

      dispatch({
        type: GeneralReducerAction.INCREMENT_FILTERS_CHANGE_COUNT,
        payload: undefined,
      })

      if (!currentBrand)
        fetchCatalogBrand({ selectedDynamicFilters: data.selectedDynamicFilters || [] })
    }
  })

  return fetchCatalogData
}

export default useFetchCatalogData
